import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { WebsiteEntity } from './website.entity';

/**
 * `queued → merging → building → live`, or `failed`. A revert is its own publish row whose
 * `fkWebsitePublishIdReverted` points at the publish it undid; that original row becomes
 * `reverted`.
 *
 * Stored as varchar rather than a MySQL ENUM on purpose: database sync is additive and
 * never alters an existing column, so adding a status to an ENUM would need a migration on
 * every tenant.
 */
export const WEBSITE_PUBLISH_STATUSES = [
	'queued',
	'merging',
	'building',
	'live',
	'failed',
	'reverted',
] as const;

export type WebsitePublishStatus = (typeof WEBSITE_PUBLISH_STATUSES)[number];

/** Statuses in which a publish is still in flight — a second publish is refused meanwhile. */
export const WEBSITE_PUBLISH_ACTIVE_STATUSES: ReadonlyArray<WebsitePublishStatus> =
	['queued', 'merging', 'building'];

export interface WebsitePublishChangeModel {
	fieldPath: string;
	label: string;
	locale: string | null;
	before: string | null;
	after: string | null;
}

/** One publish (or revert) of a website: what went live, onto what, requested by whom. */
@Index('fk_website__id', ['fkWebsiteId'], {})
@Entity('website_publish')
export class WebsitePublishEntity extends BaseEntity {
	@Column('int', { name: 'fk_website__id', unsigned: true })
	public fkWebsiteId!: number;

	@Column('varchar', { name: 'status', length: 24, default: 'queued' })
	public status!: WebsitePublishStatus;

	// The draft commit that was published, the live commit it was merged onto, and the
	// resulting merge commit on the publish branch.
	@Column('varchar', { name: 'draftSha', nullable: true, length: 64 })
	public draftSha!: string | null;

	@Column('varchar', { name: 'baseSha', nullable: true, length: 64 })
	public baseSha!: string | null;

	@Column('varchar', { name: 'mergeSha', nullable: true, length: 64 })
	public mergeSha!: string | null;

	@Column('varchar', { name: 'deploymentUrl', nullable: true, length: 500 })
	public deploymentUrl!: string | null;

	// Field-level list of what changed (values truncated). Never the content itself.
	@Column('json', { name: 'changeSummary', nullable: true })
	public changeSummary!: Array<WebsitePublishChangeModel> | null;

	// Optional note typed in the publish / revert dialog.
	@Column('varchar', { name: 'note', nullable: true, length: 500 })
	public note!: string | null;

	// TENANT user id (`user._id`), hence the fk_ name — `…_userId` columns hold global ids.
	@Column('int', {
		name: 'fk_user__id_requester',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdRequester!: number | null;

	@Column('int', {
		name: 'fk_website_publish__id_reverted',
		nullable: true,
		unsigned: true,
	})
	public fkWebsitePublishIdReverted!: number | null;

	// How many times the deployment has been polled; bounds the tracking job.
	@Column('int', {
		name: 'trackingAttempts',
		unsigned: true,
		default: () => "'0'",
	})
	public trackingAttempts!: number;

	@Column('datetime', { name: 'requestedAt' })
	public requestedAt!: Date;

	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	@Column('varchar', { name: 'errorMessage', nullable: true, length: 1000 })
	public errorMessage!: string | null;

	@ManyToOne(() => WebsiteEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_website__id', referencedColumnName: 'id' }])
	public website!: WebsiteEntity;
}
